import type { ReactElement } from 'react';

/**
 * Icons for the phone's bottom navigation bar.
 *
 * Inline SVG rather than an icon package: the app has to work with no signal,
 * and nine glyphs is not worth a dependency. They are stroke-only at 22px so
 * they sit on the 40px touch target without crowding the label under them.
 */

const common = {
  width: 22,
  height: 22,
  viewBox: '0 0 24 24',
  fill: 'none',
  stroke: 'currentColor',
  strokeWidth: 1.8,
  strokeLinecap: 'round' as const,
  strokeLinejoin: 'round' as const,
  'aria-hidden': true,
};

/** Approvals — a clipboard with a tick. */
function Approvals() {
  return (
    <svg {...common}>
      <path d="M9 3h6a1 1 0 0 1 1 1v1H8V4a1 1 0 0 1 1-1Z" />
      <path d="M16 5h2a1 1 0 0 1 1 1v13a1 1 0 0 1-1 1H6a1 1 0 0 1-1-1V6a1 1 0 0 1 1-1h2" />
      <path d="m9 13 2 2 4-4" />
    </svg>
  );
}

/** Labour — the men on site. */
function Labour() {
  return (
    <svg {...common}>
      <circle cx="9" cy="8" r="3" />
      <path d="M3 20v-1a5 5 0 0 1 5-5h2a5 5 0 0 1 5 5v1" />
      <path d="M16 5.5a3 3 0 0 1 0 5" />
      <path d="M17.5 14a5 5 0 0 1 3.5 4.8V20" />
    </svg>
  );
}

/** Diesel — a fuel drop. */
function Diesel() {
  return (
    <svg {...common}>
      <path d="M12 3s5.5 5.4 5.5 9.4A5.5 5.5 0 0 1 12 18a5.5 5.5 0 0 1-5.5-5.6C6.5 8.4 12 3 12 3Z" />
      <path d="M9.5 13a2.5 2.5 0 0 0 2.5 2.4" />
    </svg>
  );
}

/** Misc — a receipt. */
function Misc() {
  return (
    <svg {...common}>
      <path d="M6 3h12v18l-2-1.4-2 1.4-2-1.4L10 21l-2-1.4L6 21V3Z" />
      <path d="M9.5 8h5M9.5 12h5" />
    </svg>
  );
}

/** Report — a printed sheet. */
function Report() {
  return (
    <svg {...common}>
      <path d="M7 3h7l4 4v14a1 1 0 0 1-1 1H7a1 1 0 0 1-1-1V4a1 1 0 0 1 1-1Z" />
      <path d="M14 3v4h4" />
      <path d="M9 13h6M9 17h4" />
    </svg>
  );
}

/** Summary — the stacked bars. */
function Summary() {
  return (
    <svg {...common}>
      <path d="M4 20h16" />
      <rect x="6" y="11" width="3.5" height="6" rx="1" />
      <rect x="11.5" y="7" width="3.5" height="10" rx="1" />
      <rect x="17" y="13" width="3.5" height="4" rx="1" />
    </svg>
  );
}

/** Pay run — notes of money. */
function Pay() {
  return (
    <svg {...common}>
      <rect x="3" y="6" width="18" height="12" rx="2" />
      <circle cx="12" cy="12" r="2.5" />
      <path d="M6.5 9.5h.01M17.5 14.5h.01" />
    </svg>
  );
}

/** Settings — a gear. */
function Settings() {
  return (
    <svg {...common}>
      <circle cx="12" cy="12" r="3" />
      <path d="M12 3v2m0 14v2M3 12h2m14 0h2M5.6 5.6l1.4 1.4m10 10 1.4 1.4m0-12.8-1.4 1.4m-10 10-1.4 1.4" />
    </svg>
  );
}

/** Permits — a certificate with a seal. */
function Permits() {
  return (
    <svg {...common}>
      <path d="M14 3H6a1 1 0 0 0-1 1v16a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1v-9" />
      <path d="M8 8h5M8 12h4" />
      <circle cx="17" cy="6" r="3" />
      <path d="M15.5 8.5 14.5 12l2.5-1 2.5 1-1-3.5" />
    </svg>
  );
}

const ICONS: Record<string, () => ReactElement> = {
  approvals: Approvals,
  labour: Labour,
  diesel: Diesel,
  misc: Misc,
  report: Report,
  summary: Summary,
  pay: Pay,
  permits: Permits,
  settings: Settings,
};

export default function TabIcon({ name }: { name: string }) {
  const Icon = ICONS[name] ?? Report;
  return <Icon />;
}
